<template>
  <DropDownSelect
    v-model="val"
    :options="dropdownOptions"
    option-label="label"
    option-value="value"
    class="w-40 scale-75 origin-left"
    @update:model-value="handleChange"
  />
</template>

<script setup lang="ts">
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  type LexicalEditor,
} from "lexical";
import { $wrapNodes } from "@lexical/selection";
import type { HeadingTagType } from "@lexical/rich-text";
import { $createHeadingNode, $createQuoteNode } from "@lexical/rich-text";
import {
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import { blockTypeToBlockName } from "./shared";
import DropDownSelect from "@/components/shared/DropDownSelect.vue";

const props = defineProps<{
  editor: LexicalEditor;
  blockType: keyof typeof blockTypeToBlockName;
}>();

function formatParagraph() {
  if (props.blockType !== "paragraph") {
    props.editor.update(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection))
        $wrapNodes(selection, () => $createParagraphNode());
    });
  }
}

function formatHeading(headingSize: HeadingTagType) {
  if (props.blockType !== headingSize) {
    props.editor.update(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        $wrapNodes(selection, () => $createHeadingNode(headingSize));
      }
    });
  }
}

function formatBulletList() {
  if (props.blockType !== "bullet")
    props.editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
  else props.editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
}

function formatCheckList() {
  if (props.blockType !== "check")
    props.editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
  else props.editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
}

function formatNumberedList() {
  if (props.blockType !== "number")
    props.editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
  else props.editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
}

function formatQuote() {
  if (props.blockType !== "quote") {
    props.editor.update(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) $wrapNodes(selection, () => $createQuoteNode());
    });
  }
}

const val = ref("paragraph");

const dropdownOptions = [
  {
    label: "Normal",
    value: "paragraph",
  },
  {
    label: "Heading 1",
    value: "h1",
  },
  {
    label: "Heading 2",
    value: "h2",
  },
  {
    label: "Heading 3",
    value: "h3",
  },
  {
    label: "Bullet List",
    value: "bullet",
  },
  {
    label: "Numbered List",
    value: "number",
  },
  {
    label: "Check List",
    value: "check",
  },
  {
    label: "Quote",
    value: "quote",
  },
];

function handleChange(value: string) {
  console.log("Selected value:", value);
  switch (value) {
    case "paragraph":
      formatParagraph();
      break;
    case "h1":
      formatHeading("h1");
      break;
    case "h2":
      formatHeading("h2");
      break;
    case "h3":
      formatHeading("h3");
      break;
    case "bullet":
      formatBulletList();
      break;
    case "number":
      formatNumberedList();
      break;
    case "check":
      formatCheckList();
      break;
    case "quote":
      formatQuote();
      break;
  }
}
</script>
